import { useEffect } from "react";
import { Navigate, Route } from "react-router-dom";
import { setAuthorization } from "../helpers/api_helper";
import { useDispatch, useSelector } from "react-redux";

import { useProfile } from "Common/Hooks/UserHooks";
import { usePermission } from "../hooks/usePermission";

const VerifyAuthPermissions = (props: any) => {
  const { permissions } = props;
  const { hasPermission } = usePermission();
  
  // Get permissions loading state and user from Redux
  const permissionsLoading = useSelector((state: any) => state.Login?.permissionsLoading ?? false);
  const permissionsFetched = useSelector((state: any) => state.Login?.permissionsFetched ?? false);
  const userPermissions = useSelector((state: any) => state.Login?.user?.permissions ?? []);

  // If no permissions are defined for the route, allow access
  if (!permissions) {
    return <>{props.children}</>;
  }

  // Check if permissions exist in Redux state
  const hasPermissionsInState = userPermissions && userPermissions.length > 0;

  // Wait for permissions to be fetched before checking
  // This prevents redirecting to dashboard when permissions are still loading
  // Only wait if permissions are loading AND we don't have permissions in state yet
  if (permissionsLoading && !hasPermissionsInState) {
    // Return null or a loading indicator while permissions are being fetched
    // This prevents the redirect until permissions are loaded
    return null;
  }

  // If permissions haven't been fetched yet and we don't have them in state, wait
  // This handles the case where permissions are being fetched for the first time
  if (!permissionsFetched && !hasPermissionsInState) {
    return null;
  }

  // If permissions are defined, check if user has the required permission
  const { app_name, module_name, action } = permissions;
  const userHasPermission = hasPermission(app_name, module_name, action);

  if (!userHasPermission) {
    return <Navigate to="/dashboard" />;
  }

  return <>{props.children}</>;
};

const AuthProtected = (props: any) => {
  const dispatch = useDispatch<any>();
  const { userProfile, loading, token } = useProfile();
  useEffect(() => {
    if (userProfile && !loading && token) {
      setAuthorization(token);
    }
    // Only logout if we're not loading AND we have no user data
    // This prevents logout during hot reloads when localStorage might be temporarily unavailable
  }, [token, userProfile, loading, dispatch]);

  /*
    Navigate is un-auth access protected routes via url
    */
  if (!userProfile && loading && !token) {
    return <Navigate to={{ pathname: "/login" }} />;
  }

  return (
    <>
      <VerifyAuthPermissions permissions={props.permissions}>
        {props.children}
      </VerifyAuthPermissions>
    </>
  );
};

const AccessRoute = ({ component: Component, ...rest }: any) => {
  return (
    <Route
      {...rest}
      render={(props: any) => {
        return (
          <>
            {" "}
            <Component {...props} />{" "}
          </>
        );
      }}
    />
  );
};

export { AuthProtected, AccessRoute };
